/** Their page's icons, drawn as the prototype draws them (24-unit strokes in the current colour). */
import type { ReactNode } from "react";

const HEART = "M12 20.3s-7.6-4.5-9.4-9.3C1.4 7.8 3.5 4.4 7 4.4c2 0 3.7 1.1 5 2.9 1.3-1.8 3-2.9 5-2.9 3.5 0 5.6 3.4 4.4 6.6-1.8 4.8-9.4 9.3-9.4 9.3z";

// Why the style as well as the attributes: the app's sheet strokes and sizes every `.btn svg` (index.css), and an
// attribute loses to it, so a filled play mark on a button would be drawn as an outline, and at the wrong size.
function Sv({ s, w = 1.8, children }: { s: number; w?: number; children: ReactNode }): ReactNode {
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={w}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: s, height: s, fill: "none", stroke: "currentColor", strokeWidth: w }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
function Fill({ s, children }: { s: number; children: ReactNode }): ReactNode {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" style={{ width: s, height: s, fill: "currentColor", stroke: "none" }} aria-hidden="true">
      {children}
    </svg>
  );
}

type I = { s?: number };
export const IcHeart = ({ s = 18 }: I) => <Sv s={s}><path d={HEART} /></Sv>;
export const IcHeartF = ({ s = 15 }: I) => <Fill s={s}><path d={HEART} /></Fill>;
export const IcReply = ({ s = 18 }: I) => <Sv s={s}><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 6 6v4" /></Sv>;
export const IcPlus = ({ s = 20 }: I) => <Sv s={s} w={2}><path d="M12 5v14M5 12h14" /></Sv>;
export const IcUp = ({ s = 18 }: I) => <Sv s={s} w={2.4}><path d="M12 19V5M6 11l6-6 6 6" /></Sv>;
export const IcDown = ({ s = 18 }: I) => <Sv s={s} w={2.2}><path d="M12 5v14M6 13l6 6 6-6" /></Sv>;
export const IcChev = ({ s = 16 }: I) => <Sv s={s} w={2}><path d="m9 6 6 6-6 6" /></Sv>;
export const IcBack = ({ s = 20 }: I) => <Sv s={s} w={2.2}><path d="m15 6-6 6 6 6" /></Sv>;
export const IcX = ({ s = 16 }: I) => <Sv s={s} w={2.2}><path d="M6 6l12 12M18 6 6 18" /></Sv>;
export const IcPlay = ({ s = 22 }: I) => (
  <Fill s={s}><path d="M8 5.6v12.8c0 .8.9 1.3 1.6.9l10.2-6.4c.6-.4.6-1.3 0-1.7L9.6 4.8C8.9 4.3 8 4.8 8 5.6z" /></Fill>
);
export const IcPause = ({ s = 22 }: I) => (
  <Fill s={s}><rect x="6" y="5" width="4" height="14" rx="1.2" /><rect x="14" y="5" width="4" height="14" rx="1.2" /></Fill>
);
export const IcAgain = ({ s = 20 }: I) => <Sv s={s} w={2}><path d="M4 12a8 8 0 1 0 2.4-5.7" /><path d="M4 4v4h4" /></Sv>;
export const IcDl = ({ s = 17 }: I) => <Sv s={s} w={2}><path d="M12 4v11M7 10l5 5 5-5" /><path d="M5 20h14" /></Sv>;
export const IcCheck = ({ s = 17 }: I) => <Sv s={s} w={2.4}><path d="m5 12.5 4.5 4.5L19 7.5" /></Sv>;
export const IcCal = ({ s = 18 }: I) => <Sv s={s}><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /></Sv>;
export const IcCalSub = ({ s = 18 }: I) => <Sv s={s}><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4M9 15l2 2 4-4" /></Sv>;
export const IcDoc = ({ s = 20 }: I) => <Sv s={s}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></Sv>;
export const IcChat = ({ s = 20 }: I) => <Sv s={s}><path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4A8 8 0 1 1 20 12z" /><path d="M9 11h6M9 14h3" /></Sv>;
export const IcLink = ({ s = 14 }: I) => (
  <Sv s={s} w={2}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></Sv>
);
export const IcImage = ({ s = 18 }: I) => <Sv s={s}><rect x="3.5" y="4.5" width="17" height="15" rx="3" /><circle cx="9" cy="10" r="1.8" /><path d="m20 16-4.5-4.5L7 20" /></Sv>;
export const IcCamera = ({ s = 18 }: I) => (
  <Sv s={s}><path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.8l1.4-2h4.6l1.4 2h1.8A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5z" /><circle cx="12" cy="12.5" r="3.4" /></Sv>
);
export const IcPerson = ({ s = 34 }: I) => <Sv s={s} w={1.6}><circle cx="12" cy="8.5" r="3.8" /><path d="M4.5 20a7.5 7.5 0 0 1 15 0" /></Sv>;
export const IcPin = ({ s = 15 }: I) => <Sv s={s} w={1.9}><path d="M9 3.5h6l-.9 5.2 3.4 3.3v2H6.5v-2l3.4-3.3z" /><path d="M12 14v6.5" /></Sv>;
export const IcInfo = ({ s = 20 }: I) => <Sv s={s} w={1.9}><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5.5" /><circle cx="12" cy="7.8" r=".6" fill="currentColor" /></Sv>;
export const IcCDown = ({ s = 14 }: I) => <Sv s={s} w={2.2}><path d="m6 9 6 6 6-6" /></Sv>;
export const IcCUp = ({ s = 14 }: I) => <Sv s={s} w={2.2}><path d="m6 15 6-6 6 6" /></Sv>;
export const IcCopy = ({ s = 16 }: I) => (
  <Sv s={s}><rect x="8.5" y="8.5" width="11" height="11" rx="2.6" /><path d="M15.5 5.5V5A1.5 1.5 0 0 0 14 3.5H6A2.5 2.5 0 0 0 3.5 6v8A1.5 1.5 0 0 0 5 15.5h.5" /></Sv>
);
export const IcVideo = ({ s = 18 }: I) => <Sv s={s}><rect x="3" y="6.5" width="12.5" height="11" rx="2.6" /><path d="m15.5 10.5 5-3v9l-5-3" /></Sv>;
